

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewCMSPublisherModal from 'components/NewCMSPublisherModal';

const CMSPublishers = () => {
    const [publishers, setPublishers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('cms_publishers');

    useEffect(() => {
        fetchPublishers();
    }, []);

    const fetchPublishers = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/cms-publishers/');
            setPublishers(response.data);
        } catch (error) {
            console.error('Error fetching CMS publishers:', error);
            setSnackbar({ open: true, message: 'Error loading CMS publishers', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSavePublisher = async (payload) => {
        try {
            await axiosInstance.post('/cms-publishers/', payload);
            setSnackbar({ open: true, message: 'CMS Publisher created successfully', severity: 'success' });
            fetchPublishers();
        } catch (error) {
            console.error('Error saving CMS publisher:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/cms-publisher/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Name",
            accessor: "name",
            width: "20%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        { Header: "Title", accessor: "title", width: "20%" },
        { Header: "Target Section", accessor: "target_section", width: "15%" },
        {
            Header: "Trip",
            accessor: "trip_id",
            width: "15%",
            Cell: ({ row }) => row.original.trip?.name || row.original.trip_id
        },
        {
            Header: "On Hero",
            accessor: "on_hero",
            width: "10%",
            Cell: ({ value }) => value ? 'Yes' : 'No'
        },
        {
            Header: "Tags",
            accessor: "tags",
            width: "10%",
            Cell: ({ value }) => (Array.isArray(value) ? value.join(', ') : value)
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "10%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: publishers,
    }), [publishers]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">CMS Publishers</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Publisher
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : publishers.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="CMS Publisher" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewCMSPublisherModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSavePublisher}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default CMSPublishers;
